export type PetNeed = 'satiety' | 'health' | 'mood' | 'energy';
export type CareReward = 'feed' | 'play' | 'pet' | 'groom' | 'comfort';
export type PetAction = CareReward | 'rest' | 'wake' | 'bell' | 'toy' | import('./dracula').DraculaAction;
export interface PetCare {
  satiety: number;
  health: number;
  mood: number;
  energy: number;
  lastRewards: Partial<Record<CareReward, number>>;
}

export const PET_NEEDS: { id: PetNeed; label: string; color: string }[] = [
  { id: 'satiety', label: 'Сытость', color: '#d89442' },
  { id: 'health', label: 'Здоровье', color: '#50a781' },
  { id: 'mood', label: 'Настроение', color: '#c579a7' },
  { id: 'energy', label: 'Энергия', color: '#7b8fce' },
];
export const defaultPetCare = (): PetCare => ({ satiety: 78, health: 92, mood: 82, energy: 76, lastRewards: {} });
const clamp = (value: number, min = 0) => Math.max(min, Math.min(100, value));
const rewards: Record<CareReward, { cooldown: number; delta: Partial<Record<PetNeed, number>> }> = {
  feed: { cooldown: 20_000, delta: { satiety: 28, health: 3, mood: 5 } },
  play: { cooldown: 12_000, delta: { mood: 14, energy: -8, satiety: -3, health: 1 } },
  pet: { cooldown: 8000, delta: { mood: 5 } },
  groom: { cooldown: 20_000, delta: { health: 6, mood: 8 } },
  comfort: { cooldown: 10_000, delta: { mood: 14, health: 1 } },
};

export function normalizePetCare(value: unknown, now = Date.now()): PetCare {
  const result = defaultPetCare();
  const raw = value && typeof value === 'object' ? value as Record<string, unknown> : {};
  for (const { id } of PET_NEEDS) {
    const number = raw[id];
    if (typeof number === 'number' && Number.isFinite(number)) result[id] = clamp(number, id === 'health' ? 30 : 0);
  }
  const last = raw.lastRewards && typeof raw.lastRewards === 'object' ? raw.lastRewards as Record<string, unknown> : {};
  for (const action of Object.keys(rewards) as CareReward[]) {
    const timestamp = last[action];
    if (typeof timestamp === 'number' && Number.isFinite(timestamp) && timestamp >= 0) result.lastRewards[action] = Math.min(now, timestamp);
  }
  return result;
}

/** Только время видимой сессии; пропущенные дни не превращаются в голод и болезнь. */
export function advancePetCare(care: PetCare, elapsedMs: number, asleep: boolean): PetCare {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return care;
  const minutes = Math.min(elapsedMs, 60_000) / 60_000;
  const comfortable = care.satiety >= 30 && care.energy >= 25 && care.mood >= 25;
  return {
    ...care,
    satiety: clamp(care.satiety - minutes * (asleep ? .12 : .3)),
    energy: clamp(care.energy + minutes * (asleep ? 3 : -.4)),
    mood: clamp(care.mood + minutes * (asleep ? .35 : care.satiety < 20 || care.energy < 20 ? -.7 : -.2)),
    health: clamp(care.health + minutes * (comfortable ? .12 : -.1), 30),
  };
}

/** Награда только за завершённую сцену; повторный клик не начисляет её дважды. */
export function rewardPetCare(care: PetCare, action: CareReward, now = Date.now()): PetCare {
  const reward = rewards[action];
  const previous = care.lastRewards[action];
  if (previous !== undefined && now - previous < reward.cooldown) return care;
  const result = { ...care, lastRewards: { ...care.lastRewards, [action]: now } };
  for (const { id } of PET_NEEDS) result[id] = clamp(care[id] + (reward.delta[id] ?? 0), id === 'health' ? 30 : 0);
  return result;
}
